import { Auth0Callback, Auth0Error, Captcha, CatpchaConfiguration } from 'auth0-js';

export enum AuthenticationSocialProvider {
  Google,
  Facebook,
  Apple
}

/**
 * @export
 * @interface Auth0Connection
 */
export interface Auth0Connection {
  /**
   * @property
   * @type {string}
   * @memberof Auth0Connection
   */
  id: AuthenticationSocialProvider;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Connection
   */
  connection: string;
}

/**
 * @export
 * @interface Auth0Setting
 */
export interface Auth0Setting {
  /**
   * @property
   * @type {string}
   * @memberof Auth0Setting
   */
  auth0Settings: string;
  /**
   * @property
   * @type {number}
   * @memberof Auth0Setting
   */
  leeway: number;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Setting
   */
  audience: string;
  /**
   * @property
   * @type {Array<Auth0Connection>}
   * @memberof Auth0Setting
   */
  connections: Array<Auth0Connection>;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Setting
   */
  auth0Tenant: string;
}

/**
 * Contract for Auth0 configuration
 *
 * @export
 * @interface Auth0Config
 */
export interface Auth0Config {
  /**
   * @property
   * @type {string}
   * @memberof Auth0Config
   */
  domain: string;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Config
   */
  clientId: string;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Config
   */
  callbackUrl: string;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Config
   */
  responseType: string;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Config
   */
  scope: string;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Config
   */
  connection: string;
  /**
   * @property
   * @type {number}
   * @memberof Auth0Config
   */
  leeway: number;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Config
   */
  audience: string;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Config
   */
  csrf: string;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Config
   */
  state: string;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Config
   */
  protocol: string;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Config
   */
  nonce: string;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Config
   */
  brand: string;
  /**
   * allowed connections
   * @property
   * @type {string}
   * @memberof Auth0Config
   */
  connections: Array<Auth0Connection>;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Config
   */
  site: string;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Config
   */
  tenant: string;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Config
   */
  wauth: string;
  /**
   * @property
   * @type {string}
   * @memberof Auth0Config
   */
  authorizationServerIssuer: string;
}

/**
* Auth0 authentication contract

* @export
* @interface Auth0Client
*/
export interface Auth0Client {
  /**
   * Start authentication session
   * @memberof Auth0Client
   */
  login(
    email: string,
    password: string,
    callback: Auth0Callback<Auth0Error>,
    captcha?: string
  ): void;

  /**
   * Create user
   * @memberof Auth0Client
   */
  signUp(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    exclusiveEmails: boolean,
    fewerEmails: boolean,
    signUpLocale: string,
    site: string,
    callback: Auth0Callback<Auth0Error>,
    captcha?: string
  ): void;

  /**
   * Change clients password
   * @memberof Auth0Client
   */
  changePassword(email: string, callback: Auth0Callback<Auth0Error>): void;

  /**
   * Authenticate with supported social providers
   * @param provider
   * @param loginHint
   * @memberof Auth0Client
   */
  authorizeSocial(
    provider: AuthenticationSocialProvider,
    loginHint?: string
  ): void;

  /**
   * 
   * @param element 
   * @param options 
   * @param callback 
   */
  renderCaptcha(element: HTMLElement, options?: CatpchaConfiguration, callback?: Auth0Callback<any>): Captcha;
}

/**
 * Contract for Auth0 reset configuration
 *
 * @export
 * @interface Auth0ResetConfig
 */
export interface Auth0ResetConfig {
  resetEmailEndpoint: string;
  resendResetEmailEndpoint: string;
  email: string;
  csrfToken: string;
  ticket: string;
  connection: string;
}

export enum ResetPasswordResult {
  success,
  weak,
  fail
}

/**
* Auth0 reset password contract

* @export
* @interface Auth0ResetClient
*/
export interface Auth0ResetClient {
  /**
   * Change clients password
   * @memberof Auth0ResetClient
   */
  resetPassword(password: string): Promise<ResetPasswordResult>;

  /**
   * Sends reset email to client
   * @param email
   */
  resendResetEmail(email: string): Promise<void>;
}
